// Die Uhr des Geraets lesen und setzen (get_info / set_time).
//
// Hotel-TVs ohne NTP laufen mit der Zeit auseinander, und eine falsche Uhr
// sieht man erst, wenn EPG, Wecker oder Timer danebenliegen. Aus der Ferne
// setzen spart den Gang mit der Service-Fernbedienung.
//
// Wege, in dieser Reihenfolge - aufgerufen wird nur, was es am Geraet
// WIRKLICH gibt (wie in DeviceSettings: vorher pruefen, nie blind versuchen):
//
//  Samsung
//   1. `webapis.timer.setCurrentTime({year, month, day, hour, minute, second})`
//      - Samsung Product API ab Tizen 6.5, synchron, wirft bei Fehler.
//      Braucht das Partner-Privileg `developer.samsung.com/privilege/devicetimer`
//      in der config.xml der WGT.
//   2. `b2bapis.b2bcontrol.setCurrentTime("YYYY:MM:DD:HH:MM:SS", ok, err)`
//      - der Vorgaenger (SSSP / Tizen 4), mit Callbacks.
//  LG
//   3. `hcap.time.setLocalTime({year, month, day, hour, minute, second, ...})`
//      - gmtOffsetInMinute und isDaylightSaving werden aus getLocalTime
//      uebernommen, damit sich die Zeitzone des TV nicht mitaendert.
//
// Gesetzt wird die WANDUHRZEIT, die der Admin schickt, nicht ein Zeitpunkt:
// die Uhr im Menue eines Hotel-TV ist eine Ortszeit, und ob die JS-Zeitzone
// des Geraets zur Menue-Zeitzone passt, ist gerade auf Samsung nicht sicher.

import { hasHcapMethod, hcapCall } from '@/helpers/LgHcap';

const CALLBACK_TIMEOUT_MS = 3000;

function pad(n, len) {
  let s = String(n);
  while (s.length < (len || 2)) s = '0' + s;
  return s;
}

function num(v) {
  // Ohne Number.isFinite/Math.trunc - Orsay kennt beides nicht sicher.
  const n = Number(v);
  if (v === '' || v === null || typeof v === 'boolean' || !isFinite(n)) return NaN;
  return n < 0 ? Math.ceil(n) : Math.floor(n);
}

// Die Wanduhrzeit aus der Nutzlast pruefen. null, wenn etwas nicht passt - dann
// wird gar nichts gesetzt, statt ein halbes Datum an die Firmware zu geben.
export function normalizeTime(t) {
  if (!t || typeof t !== 'object') return null;
  const out = {
    year: num(t.year),
    month: num(t.month),
    day: num(t.day),
    hour: num(t.hour),
    minute: num(t.minute),
    second: t.second === undefined || t.second === null ? 0 : num(t.second),
  };
  if (!(out.year >= 2000 && out.year <= 2099)) return null;
  if (!(out.month >= 1 && out.month <= 12)) return null;
  const daysInMonth = new Date(out.year, out.month, 0).getDate();
  if (!(out.day >= 1 && out.day <= daysInMonth)) return null;
  if (!(out.hour >= 0 && out.hour <= 23)) return null;
  if (!(out.minute >= 0 && out.minute <= 59)) return null;
  if (!(out.second >= 0 && out.second <= 59)) return null;
  return out;
}

// "YYYY:MM:DD:HH:MM:SS" - das Format von b2bcontrol.setCurrentTime.
export function b2bTimeString(t) {
  return [pad(t.year, 4), pad(t.month), pad(t.day), pad(t.hour), pad(t.minute), pad(t.second)].join(':');
}

// "YYYY-MM-DD HH:MM:SS" - zum Anzeigen und fuer das Log.
export function formatTime(t) {
  if (!t) return '';
  return (
    pad(t.year, 4) + '-' + pad(t.month) + '-' + pad(t.day) + ' ' +
    pad(t.hour) + ':' + pad(t.minute) + ':' + pad(t.second || 0)
  );
}

// Die Uhr, wie sie eine Firmware zurueckgibt, auf {year, …, second} bringen.
// Samsung liefert ein TimeSetting-Objekt, der b2bcontrol-Getter je nach Modell
// einen String im Setter-Format, LG ein Objekt mit denselben Feldnamen.
export function parseDeviceTime(raw) {
  if (!raw) return null;
  if (typeof raw === 'string') {
    const m = raw.match(/(\d{4})\D(\d{1,2})\D(\d{1,2})\D(\d{1,2})\D(\d{1,2})(?:\D(\d{1,2}))?/);
    if (!m) return null;
    return normalizeTime({
      year: m[1], month: m[2], day: m[3], hour: m[4], minute: m[5], second: m[6] || 0,
    });
  }
  if (typeof raw === 'object') return normalizeTime(raw);
  return null;
}

function webapisTimer() {
  try {
    const w = typeof window !== 'undefined' ? window.webapis : undefined;
    const t = w && w.timer;
    return t && typeof t.setCurrentTime === 'function' ? t : undefined;
  } catch (e) {
    return undefined;
  }
}

function b2bControl() {
  try {
    const b = typeof window !== 'undefined' ? window.b2bapis : undefined;
    const c = b && b.b2bcontrol;
    return c && typeof c.setCurrentTime === 'function' ? c : undefined;
  } catch (e) {
    return undefined;
  }
}

function errText(e) {
  if (!e) return 'unbekannter Fehler';
  if (typeof e === 'string') return e;
  const parts = [];
  if (e.name) parts.push(e.name);
  if (e.code !== undefined) parts.push('code ' + e.code);
  if (e.message) parts.push(e.message);
  if (e.errorMessage) parts.push(e.errorMessage);
  if (parts.length) return parts.join(' - ');
  try {
    return JSON.stringify(e);
  } catch (x) {
    return String(e);
  }
}

// Die Uhr aus dem Geraetemenue lesen. { time, via } oder null.
async function readMenuClock() {
  const timer = webapisTimer();
  if (timer && typeof timer.getCurrentTime === 'function') {
    try {
      const t = parseDeviceTime(timer.getCurrentTime());
      if (t) return { time: t, via: 'webapis.timer' };
    } catch (e) {
      /* naechster Weg */
    }
  }
  const b2b = b2bControl();
  if (b2b && typeof b2b.getCurrentTime === 'function') {
    try {
      const t = parseDeviceTime(b2b.getCurrentTime());
      if (t) return { time: t, via: 'b2bapis.b2bcontrol' };
    } catch (e) {
      /* naechster Weg */
    }
  }
  if (hasHcapMethod('time', 'getLocalTime')) {
    try {
      const raw = await hcapCall('time', 'getLocalTime', {}, CALLBACK_TIMEOUT_MS);
      const t = parseDeviceTime(raw);
      if (t) return { time: t, via: 'hcap.time', raw: raw };
    } catch (e) {
      /* kein Weg */
    }
  }
  return null;
}

// Was die Info-Antwort ueber die Uhr traegt.
//
// `ts` ist die JS-Zeit des Geraets im Moment der Abfrage - der Admin rechnet
// daraus die Abweichung zu seiner eigenen Uhr. `menu` ist die Uhr aus dem
// Geraetemenue, soweit eine Firmware sie herausgibt; sie kann von der JS-Zeit
// abweichen, wenn die Zeitzonen nicht zusammenpassen.
export async function read() {
  // Erst die Menue-Uhr (auf LG ein Callback), dann die JS-Zeit - so passen
  // ts, local und menu zeitlich zusammen.
  let menu = null;
  try {
    menu = await readMenuClock();
  } catch (e) {
    menu = null;
  }
  const now = new Date();
  return {
    ts: now.getTime(),
    tzOffsetMinutes: -now.getTimezoneOffset(),
    local: formatTime({
      year: now.getFullYear(),
      month: now.getMonth() + 1,
      day: now.getDate(),
      hour: now.getHours(),
      minute: now.getMinutes(),
      second: now.getSeconds(),
    }),
    menu: menu ? formatTime(menu.time) : null,
    menuVia: menu ? menu.via : null,
  };
}

function setViaB2b(b2b, t) {
  return new Promise((resolve, reject) => {
    let settled = false;
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      reject(new Error('b2bcontrol.setCurrentTime: kein Callback'));
    }, CALLBACK_TIMEOUT_MS);
    const done = (fn, arg) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      fn(arg);
    };
    try {
      b2b.setCurrentTime(
        b2bTimeString(t),
        (v) => done(resolve, v),
        (e) => done(reject, e)
      );
    } catch (e) {
      done(reject, e);
    }
  });
}

async function setViaHcap(t) {
  const params = {
    year: t.year,
    month: t.month,
    day: t.day,
    hour: t.hour,
    minute: t.minute,
    second: t.second,
  };
  // Zeitzone und Sommerzeit so lassen, wie sie am TV stehen.
  try {
    const cur = await hcapCall('time', 'getLocalTime', {}, CALLBACK_TIMEOUT_MS);
    if (cur && cur.gmtOffsetInMinute !== undefined) params.gmtOffsetInMinute = cur.gmtOffsetInMinute;
    if (cur && cur.isDaylightSaving !== undefined) params.isDaylightSaving = cur.isDaylightSaving;
  } catch (e) {
    /* ohne - dann nimmt die Firmware ihre eigenen Werte */
  }
  return hcapCall('time', 'setLocalTime', params, CALLBACK_TIMEOUT_MS);
}

// Die Uhr setzen. Liefert { ok, via, wanted, before, after, tried, error }.
//
// `tried` nennt jeden versuchten Weg mit seinem Fehler: steht am Ende "ok:false",
// sieht man im Admin, ob es die API nicht gab oder ob sie abgelehnt hat - bei
// Samsung heisst "code 9" nicht vorhanden, "code 18" fehlendes Privileg.
export async function write(wantedRaw) {
  const wanted = normalizeTime(wantedRaw);
  if (!wanted) {
    return { ok: false, error: 'ungueltige Uhrzeit in der Nutzlast (time fehlt oder unvollstaendig)' };
  }
  const before = await read().catch(() => null);
  const tried = [];
  let via = null;

  const timer = webapisTimer();
  if (!via && timer) {
    try {
      timer.setCurrentTime({
        year: wanted.year,
        month: wanted.month,
        day: wanted.day,
        hour: wanted.hour,
        minute: wanted.minute,
        second: wanted.second,
      });
      via = 'webapis.timer';
    } catch (e) {
      tried.push({ via: 'webapis.timer', error: errText(e) });
    }
  }
  const b2b = b2bControl();
  if (!via && b2b) {
    try {
      await setViaB2b(b2b, wanted);
      via = 'b2bapis.b2bcontrol';
    } catch (e) {
      tried.push({ via: 'b2bapis.b2bcontrol', error: errText(e) });
    }
  }
  if (!via && hasHcapMethod('time', 'setLocalTime')) {
    try {
      await setViaHcap(wanted);
      via = 'hcap.time';
    } catch (e) {
      tried.push({ via: 'hcap.time', error: errText(e) });
    }
  }

  const after = await read().catch(() => null);
  if (!via) {
    return {
      ok: false,
      wanted: formatTime(wanted),
      before: before,
      after: after,
      tried: tried,
      error: tried.length
        ? 'kein Weg hat die Uhrzeit uebernommen'
        : 'dieses Geraet bietet keine API zum Setzen der Uhrzeit',
    };
  }
  return { ok: true, via: via, wanted: formatTime(wanted), before: before, after: after, tried: tried };
}

export default { read, write, normalizeTime, parseDeviceTime, formatTime, b2bTimeString };



// WEBPACK FOOTER //
// ./src/helpers/DeviceClock.js